import React from 'react';
import {
	Box,
	Paper,
	Typography,
	Grid,
	Card,
	CardContent,
	TextField,
	InputAdornment,
	Button,
	IconButton,
	useTheme,
	Menu,
	MenuItem,
	CircularProgress,
	Snackbar,
	Alert,
	Skeleton,
	Tooltip,
	Stack,
} from '@mui/material';
import { Refresh as RefreshIcon, DateRange as DateRangeIcon } from '@mui/icons-material';
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip as RechartsTooltip,
	Legend,
	ResponsiveContainer,
	PieChart,
	Pie,
	Cell,
} from 'recharts';
import axios from 'axios';
import CustomPieLabel from './CustomPieLabel';
import { getAllReports, getAllReportsFilter } from '../../../services/api/Reports';
import { API_BASE_URL } from '../../../utlis';

// Constants
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];
const REPORT_TYPES = {
	SALES: 'sales',
	REVENUE: 'normal',
};
const EXPORT_FORMATS = {
	PDF: 'pdf',
	EXCEL: 'csv',
};
const SALES_FORMATS = {
	Report: 'normal',
	SALES: 'sales',
};

// Type Definitions
interface ReportData {
	statusDataQuotation: { status: string; count: number; color: string }[];
	statusDataBooking: { status: string; count: number; color: string }[];
	statusDataContactUs: { status: string; count: number; color: string }[];
	timeline: { date: string; quotations: number; bookings: number; contacts: number }[];
	totalRevenueAmount: number;
	monthlyConfirmedQuotations: any[];
	yearlyConfirmedQuotations: any[];
}

interface SummaryCard {
	id: string;
	title: string;
	value: string | number;
	description: string;
	color: string;
	statusData?: { status: string; count: number; color: string }[];
}

const ReportPage: React.FC = () => {
	const theme = useTheme();
	const currentDate = new Date().toISOString().split('T')[0];
	const [startDate, setStartDate] = React.useState(currentDate);
	const [endDate, setEndDate] = React.useState(currentDate);
	const [formatMenuAnchorEl, setFormatMenuAnchorEl] = React.useState<null | HTMLElement>(null);
	const [reportTypeMenuAnchorEl, setReportTypeMenuAnchorEl] = React.useState<null | HTMLElement>(
		null
	);
	const [salesFormatMenuAnchorEl, setSalesFormatMenuAnchorEl] =
		React.useState<null | HTMLElement>(null);
	const [reportData, setReportData] = React.useState<ReportData>({
		statusDataQuotation: [],
		statusDataBooking: [],
		statusDataContactUs: [],
		timeline: [],
		totalRevenueAmount: 0,
		monthlyConfirmedQuotations: [],
		yearlyConfirmedQuotations: [],
	});
	const [loading, setLoading] = React.useState(false);

	const [snackbar, setSnackbar] = React.useState({
		open: false,
		message: '',
		severity: 'success' as 'success' | 'error' | 'info' | 'warning',
	});
	const [selectedReportType, setSelectedReportType] = React.useState<string | null>(null);
	const [salesFormat, setSalesFormat] = React.useState(SALES_FORMATS.Report);

	// Calculate totals from status data
	const totals = React.useMemo(() => {
		const quotationsTotal = reportData?.statusDataQuotation?.reduce(
			(sum, item) => sum + item?.count,
			0
		);
		const quotationsConfirmed =
			reportData.statusDataQuotation?.find((item) => item?.status === 'Confirmed')?.count ||
			0;

		const bookingsTotal = reportData?.statusDataBooking?.reduce(
			(sum, item) => sum + item?.count,
			0
		);
		const bookingsConverted = 0; // Adjust based on your data structure

		const contactsTotal = reportData?.statusDataContactUs?.reduce(
			(sum, item) => sum + item?.count,
			0
		);
		const contactsConfirmed = 0; // Adjust based on your data structure

		return {
			quotations: {
				total: quotationsTotal,
				confirmed: quotationsConfirmed,
				revenue: reportData?.totalRevenueAmount,
			},
			bookings: {
				total: bookingsTotal,
				converted: bookingsConverted,
			},
			contacts: {
				total: contactsTotal,
				confirmed: contactsConfirmed,
			},
		};
	}, [reportData]);

	// Memoized chart data
	const revenueData = React.useMemo(
		() =>
			reportData?.timeline?.map((item) => ({
				name: item?.date,
				quotations: item?.quotations,
				bookings: item?.bookings,
				contacts: item?.contacts,
			})),
		[reportData.timeline]
	);

	const eventTypeData = React.useMemo(
		() => [
			{ name: 'Quotations', value: totals?.quotations?.total },
			{ name: 'Bookings', value: totals?.bookings?.total },
			{ name: 'Contacts', value: totals?.contacts?.total },
		],
		[totals]
	);

	const summaryCards = React.useMemo(() => {
		const cards: SummaryCard[] = [];

		if (salesFormat === SALES_FORMATS.SALES) {
			cards.push({
				id: '2',
				title: 'Potential Revenue',
				value: `₹ ${totals?.quotations?.revenue?.toLocaleString() || 0}`,
				description: `From ${totals?.quotations?.confirmed} confirmed`,
				color: 'success.main',
			});
		} else {
			cards.push(
				{
					id: '1',
					title: 'Total Quotations',
					value: totals?.quotations?.total,
					description: `Confirmed: ${totals?.quotations?.confirmed}`,
					color: 'primary.main',
					statusData: reportData?.statusDataQuotation || [],
				},
				{
					id: '3',
					title: 'Bookings',
					value: totals?.bookings?.total,
					description: `Converted: ${totals?.bookings?.converted}`,
					color: 'info.main',
					statusData: reportData?.statusDataBooking || [],
				},
				{
					id: '4',
					title: 'Contacts',
					value: totals?.contacts?.total,
					description: `Contact status overview`,
					color: 'warning.main',
					statusData: reportData?.statusDataContactUs || [],
				}
			);
		}

		return cards;
	}, [totals, salesFormat, reportData]);

	const fetchReportData = async () => {
		try {
			setLoading(true);
			const response = await getAllReports();
			if (response.success) {
				setReportData(response.data);
			}
		} catch (error) {
			console.error('Error fetching report data:', error);
			showSnackbar('Failed to fetch report data', 'error');
		} finally {
			setLoading(false);
		}
	};

	const fetchReportFiltersData = async () => {
		try {
			setLoading(true);
			const response = await getAllReportsFilter(startDate, endDate, salesFormat);
			if (response.success) {
				setReportData(response.data);
			}
		} catch (error) {
			console.error('Error fetching filtered report data:', error);
			showSnackbar('Failed to fetch filtered report data', 'error');
		} finally {
			setLoading(false);
		}
	};

	React.useEffect(() => {
		if (startDate || endDate || salesFormat !== SALES_FORMATS.Report) {
			fetchReportFiltersData();
		} else {
			fetchReportData();
		}
	}, [startDate, endDate, salesFormat]);

	const showSnackbar = (
		message: string,
		severity: 'success' | 'error' | 'info' | 'warning' = 'success'
	) => {
		setSnackbar({
			open: true,
			message,
			severity,
		});
	};

	const handleExportReport = async (format: string, type: string) => {
		try {
			setFormatMenuAnchorEl(null);

			const headers = {
				'Content-Type': 'application/json',
			} as { [key: string]: string };

			const accessToken = localStorage.getItem('token');
			if (accessToken) {
				headers.Authorization = `Bearer ${accessToken}`;
			}

			const response = await axios.get(
				`${API_BASE_URL}/admin/report/export?startDate=${startDate}&endDate=${endDate}&format=${format}&reportType=${type}&type=${type}`,
				{
					headers,
					responseType: 'blob',
				}
			);

			if (response) {
				const mimeType =
					format === EXPORT_FORMATS.PDF
						? 'application/pdf'
						: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

				const blob = new Blob([response.data], { type: mimeType });
				const url = window.URL.createObjectURL(blob);

				const a = document.createElement('a');
				a.href = url;
				a.download = `${type?.toLowerCase()}_report_${new Date().toISOString().split('T')[0]}.${format}`;
				document.body.appendChild(a);
				a.click();

				window.URL.revokeObjectURL(url);
				document.body.removeChild(a);

				showSnackbar(`${type} report exported successfully as ${format.toUpperCase()}`);
			}
		} catch (error) {
			console.error('Error exporting report:', error);
			showSnackbar('Failed to export report', 'error');
		}
	};

	const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setReportTypeMenuAnchorEl(event.currentTarget);
	};

	const handleReportTypeSelect = (type: string, event: React.MouseEvent<HTMLElement>) => {
		setSelectedReportType(type);
		setReportTypeMenuAnchorEl(null);
		setFormatMenuAnchorEl(event.currentTarget); // Open the format menu after selecting the report type
	};

	const handleApplyFilters = () => {
		if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
			showSnackbar('End date must be after start date', 'error');
			return;
		}
		fetchReportFiltersData();
	};

	const handleResetFilters = () => {
		setStartDate(currentDate);
		setEndDate(currentDate);
		setSalesFormat(SALES_FORMATS.Report);
		fetchReportData();
	};

	const handleCloseSnackbar = () => {
		setSnackbar((prev) => ({ ...prev, open: false }));
	};

	return (
		<Box sx={{ p: 3, maxHeight: '90vh', overflowY: 'auto' }}>
			{/* Filters */}
			<Paper sx={{ p: 2, mb: 3 }}>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={6} md={2}>
						<TextField
							fullWidth
							size="small"
							type="date"
							label="Start Date"
							InputLabelProps={{ shrink: true }}
							value={startDate}
							onChange={(e) => setStartDate(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<DateRangeIcon />
									</InputAdornment>
								),
							}}
						/>
					</Grid>
					<Grid item xs={6} md={2}>
						<TextField
							fullWidth
							size="small"
							type="date"
							label="End Date"
							InputLabelProps={{ shrink: true }}
							value={endDate}
							onChange={(e) => setEndDate(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<DateRangeIcon />
									</InputAdornment>
								),
							}}
						/>
					</Grid>
					<Grid item xs={6} md={2}>
						<Button
							fullWidth
							variant="outlined"
							onClick={(e) => setSalesFormatMenuAnchorEl(e.currentTarget)}
							aria-label="Sales format"
						>
							Format:{' '}
							{salesFormat.charAt(0).toUpperCase() + salesFormat.slice(1) === 'Normal'
								? 'Report'
								: salesFormat.charAt(0).toUpperCase() + salesFormat.slice(1)}
						</Button>
						<Menu
							anchorEl={salesFormatMenuAnchorEl}
							open={Boolean(salesFormatMenuAnchorEl)}
							onClose={() => setSalesFormatMenuAnchorEl(null)}
						>
							<MenuItem
								onClick={() => {
									setSalesFormat(SALES_FORMATS.Report);
									setSalesFormatMenuAnchorEl(null);
								}}
								sx={{ width: '190px' }}
							>
								Report
							</MenuItem>
							<MenuItem
								sx={{ width: '190px' }}
								onClick={() => {
									setSalesFormat(SALES_FORMATS.SALES);
									setSalesFormatMenuAnchorEl(null);
								}}
							>
								Sales
							</MenuItem>
						</Menu>
					</Grid>
					<Grid item xs={6} md={1}>
						<Button
							fullWidth
							variant="contained"
							onClick={handleApplyFilters}
							disabled={loading}
							aria-label="Apply filters"
						>
							{loading ? <CircularProgress size={24} /> : 'Apply'}
						</Button>
					</Grid>
					<Grid item xs={12} md={3} sx={{ marginLeft: 'auto' }}>
						<Box>
							<Button onClick={handleExportClick}>Export Report</Button>

							<Menu
								anchorEl={reportTypeMenuAnchorEl}
								open={Boolean(reportTypeMenuAnchorEl)}
								onClose={() => setReportTypeMenuAnchorEl(null)}
							>
								<MenuItem
									onClick={(e) => handleReportTypeSelect(REPORT_TYPES.SALES, e)}
								>
									Sales Report
								</MenuItem>
								<MenuItem
									onClick={(e) => handleReportTypeSelect(REPORT_TYPES.REVENUE, e)}
								>
									Revenue Report
								</MenuItem>
							</Menu>

							<Menu
								anchorEl={formatMenuAnchorEl}
								open={Boolean(formatMenuAnchorEl)}
								onClose={() => setFormatMenuAnchorEl(null)}
							>
								<MenuItem
									onClick={() =>
										selectedReportType &&
										handleExportReport(EXPORT_FORMATS.PDF, selectedReportType)
									}
								>
									Export as PDF
								</MenuItem>
								<MenuItem
									onClick={() =>
										selectedReportType &&
										handleExportReport(EXPORT_FORMATS.EXCEL, selectedReportType)
									}
								>
									Export as Excel
								</MenuItem>
							</Menu>

							<IconButton
								onClick={handleResetFilters}
								disabled={loading}
								aria-label="Refresh data"
							>
								<RefreshIcon />
							</IconButton>
						</Box>
					</Grid>
				</Grid>
			</Paper>

			{/* Loading indicator */}
			{loading && (
				<Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
					<CircularProgress />
				</Box>
			)}

			{/* Report Content */}
			{!loading ? (
				<>
					{/* Summary Cards */}
					<Grid container spacing={3} sx={{ mb: 3 }}>
						{summaryCards.map((card) => (
							<Grid
								item
								xs={12}
								md={salesFormat === SALES_FORMATS.SALES ? 4 : 4}
								key={card.id}
							>
								<Card elevation={3}>
									<CardContent>
										<Typography color="textSecondary" gutterBottom>
											{card.title}
										</Typography>
										<Typography variant="h4" fontWeight="bold">
											{card.value}
										</Typography>
										{Array.isArray(card.statusData) &&
											card.statusData.length > 0 && (
												<Stack
													direction="row"
													spacing={1}
													mt={2}
													flexWrap="wrap"
												>
													{card.statusData.map((statusItem, i) => {
														console.log(statusItem, 'statusItem');
														return (
															<Tooltip
																key={i}
																title={`${statusItem.status}: ${statusItem.count}`}
																arrow
																placement="top"
															>
																<Box
																	sx={{
																		width: 12,
																		height: 12,
																		borderRadius: '50%',
																		bgcolor:
																			statusItem.color ||
																			'grey.400',
																		cursor: 'pointer',
																	}}
																/>
															</Tooltip>
														);
													})}
												</Stack>
											)}
									</CardContent>
								</Card>
							</Grid>
						))}
					</Grid>

					{/* Charts */}
					<Grid container spacing={3} sx={{ mb: 3 }}>
						<Grid item xs={12} md={salesFormat === SALES_FORMATS.SALES ? 12 : 8}>
							<Paper elevation={3} sx={{ p: 2, height: '400px' }}>
								<Typography variant="h6" gutterBottom>
									Timeline Activity
								</Typography>
								<ResponsiveContainer width="100%" height="90%">
									<BarChart data={revenueData}>
										<CartesianGrid strokeDasharray="3 3" />
										<XAxis dataKey="name" />
										<YAxis />
										<RechartsTooltip />
										<Legend />
										<Bar
											dataKey="quotations"
											fill={theme.palette.primary.main}
											name="Quotations"
										/>
										<Bar
											dataKey="bookings"
											fill={theme.palette.secondary.main}
											name="Bookings"
										/>
										<Bar
											dataKey="contacts"
											fill={theme.palette.error.main}
											name="Contacts"
										/>
									</BarChart>
								</ResponsiveContainer>
							</Paper>
						</Grid>

						{/* Only show Activity Distribution in normal format */}
						{salesFormat !== SALES_FORMATS.SALES && (
							<Grid item xs={12} md={4}>
								<Paper elevation={3} sx={{ p: 2, height: '400px' }}>
									<Typography variant="h6" gutterBottom>
										Activity Distribution
									</Typography>
									<ResponsiveContainer width="100%" height="90%">
										<PieChart>
											<Pie
												data={eventTypeData}
												cx="30%"
												cy="50%"
												labelLine={false}
												outerRadius={80}
												fill="#8884d8"
												style={{ margin: '12px' }}
												dataKey="value"
												nameKey="name"
												label={({ name, percent, index }: any) => (
													<CustomPieLabel
														name={name}
														percent={percent}
														index={index}
													/>
												)}
											>
												{eventTypeData.map((entry, index) => (
													<Cell
														key={`cell-${index}`}
														fill={COLORS[index % COLORS.length]}
													/>
												))}
											</Pie>
											<RechartsTooltip />
										</PieChart>
									</ResponsiveContainer>
								</Paper>
							</Grid>
						)}
					</Grid>
				</>
			) : (
				// Skeleton loading state
				<>
					<Grid container spacing={3} sx={{ mb: 3 }}>
						{[1, 2, 3, 4].map((item) => (
							<Grid item xs={12} md={3} key={item}>
								<Skeleton variant="rectangular" height={118} />
							</Grid>
						))}
					</Grid>
					<Grid container spacing={3}>
						<Grid item xs={12} md={8}>
							<Skeleton variant="rectangular" height={400} />
						</Grid>
						<Grid item xs={12} md={4}>
							<Skeleton variant="rectangular" height={400} />
						</Grid>
					</Grid>
				</>
			)}

			{/* Snackbar for notifications */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					variant="filled"
					sx={{ width: '100%' }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default ReportPage;
